import { Component, Element, Host, Prop, Event, EventEmitter, State, Watch, h } from '@stencil/core';
import { metrics, cdnpaths } from '../../utils';
import {
  Alpha,
  Priority,
  NeutralTones,
  NeutralTonesInverse,
  BrandTones,
  StatusTones,
  TShirtSizesStandard,
  TShirtSizesExtraSmall,
  TShirtSizesExtraLarge,
} from '../../utils/types/index';
import DOMPurify from 'dompurify';
/**
 * @cssprop --prism-icon-color - Setting the color of the icon, stroke or fill
 * @cssprop --prism-icon-size-width - Sets the width of the icon
 * @cssprop --prism-icon-size-height - Sets the height of the icon
 * @cssprop --prism-icon-size-contained-border-radius - Sets the border radius of the contained wrapper
 * @cssprop --prism-icon-color-border-contained - Sets the border color of the contained wrapper
 * @cssprop --prism-icon-color-background-contained - Sets the background color of the contained wrapper
 * @cssprop --prism-icon-spacing-contained-padding - Sets the padding of the contained wrapper
 * @cssprop --prism-icon-color-background-dot - Sets the background color of the notification dot
 * @cssprop --prism-icon-spacing-border-contained - Sets the border width of the contained wrapper
 **/
@Component({
  tag: 'prism-icon',
  styleUrl: 'prism-icon.css',
  shadow: true,
})
export class Icon {
  private intobs?: IntersectionObserver;
  /** @internal */
  @Element() el!: HTMLPrismIconElement;
  /** Reference to the Dialog */
  /** @internal */
  element!: HTMLElement;
  /** Event when Icon has loaded */
  @Event() prismLoad!: EventEmitter;
  /** @internal */
  @State() svgIcon!: string;
  /** @internal */
  @State() private svgContent?: string;
  /** @internal */
  @State() private isVisible = false;
  /** @internal */
  @State() private loaded = false;
  /**
   * Contained: Shows a material container around the icon
   */
  @Prop() contained: boolean = false;
  /**
   * Adds a tone to the contained wrapper of the icon
   */
  @Prop() containedTone?: NeutralTones | NeutralTonesInverse | BrandTones | StatusTones;
  /** Contained Display will toggle between background tone and border stroke tone */
  @Prop() containedDisplay: 'fill' | 'outline' = 'fill';
  /** Contained Priority */
  @Prop() containedPriority: Priority = 'low';
  /**
   * Specifies the label to use for accessibility. Defaults to the icon name.
   */
  @Prop() label?: string;
  /**
   * Specifies the role: img or presentation
   */
  @Prop() svgRole: 'img' | 'presentation' = 'img';
  /**
   * Enable the Notification Dot
   */
  @Prop() notifications: boolean = false;
  /**
   * Specifies which icon to use from the built-in set of icons.
   */
  @Prop() name: string = '';
  /**
   * The size of the icon.
   */
  @Prop() size?: TShirtSizesExtraSmall | TShirtSizesStandard | TShirtSizesExtraLarge;
  /**
   * Tones adds a specific stroke or fill color to the icon
   */
  @Prop() tone?: NeutralTones | NeutralTonesInverse | BrandTones | StatusTones;
  /**
   * The notification dot's tone
   */
  @Prop() dotTone?: NeutralTones | NeutralTonesInverse | BrandTones | StatusTones;
  /**
   * Alpha Priority
   */
  @Prop() priority: Alpha = 'primary';
  /**
   * Display: Outline or Fill
   */
  @Prop() display?: 'outline' | 'fill' = 'fill';
  /**
   * Path: Local or Absolute path to an SVG
   */
  @Prop() path?: string = '';
  /**
   * If enabled, prism-icon will be loaded lazily when it's visible in the viewport.
   * Default, `false`.
   */
  @Prop() lazy = false;
  /** CDN Choice */
  @Prop() cdn: 'xd' | 'illicons' | 'business' = 'xd';
  /** @private */
  @State() cdnsource: string;
  /** @private */
  @State() fetchUrl: string;
  /**
   * @description Watch for changes to multiple props call loadIcon()
   */
  @Watch('name')
  @Watch('cdn')
  @Watch('path')
  @Watch('display')
  nameHandler() {
    this._getCdn();
  }
  /** @internal
   * Get the CDN Path
   */
  _getCdn() {
    // Introducing CDN Paths for multiple brands
    this.cdnsource = cdnpaths[this.cdn];
    // If a local path is passed, we don't use the CDN at all
    if (this.path) {
      this.fetchUrl = this.path;
    } else {
      switch (this.cdn) {
        case 'xd':
          this.fetchUrl = `${this.cdnsource}${this.formatName(this.name)}/ic_${this.formatName(this.name)}_${this.display}.svg`;
          break;
        case 'illicons':
          this.fetchUrl = `${this.cdnsource}${this.formatName(this.name)}.svg`;
          break;
        case 'business':
          // Need to verify this path is accurate
          this.fetchUrl = `${this.cdnsource}${this.formatName(this.name)}.svg`;
          break;
      }
    }
    // purposely do not return the promise here because loading
    // the svg file should not hold up loading the app
    // only load the svg if it's visible
    this.waitUntilVisible(this.el, '25%', () => {
      this.isVisible = true;
      this.loadIcon();
    });
  }

  componentWillLoad() {
    this._getCdn();
  }
  connectedCallback() {
    // Component Data Layer
    metrics('prism-icon');
  }
  disconnectedCallback() {
    if (this.intobs) {
      this.intobs.disconnect();
      this.intobs = undefined;
    }
  }

  private waitUntilVisible(el: HTMLPrismIconElement, rootMargin: string, cb: () => void) {
    if (this.lazy && typeof window !== 'undefined' && (window as any).IntersectionObserver) {
      const io = (this.intobs = new (window as any).IntersectionObserver(
        (data: IntersectionObserverEntry[]) => {
          if (data[0].isIntersecting) {
            io.disconnect();
            this.intobs = undefined;
            cb();
          }
        },
        { rootMargin },
      ));

      io.observe(el);
    } else {
      // browser doesn't support IntersectionObserver
      // so just fallback to always show it
      cb();
    }
  }
  /** @internal */
  formatName(str: string) {
    let process = str.replace(' ', '_').toLocaleLowerCase();
    return process;
  }
  /** @internal */
  async loadIcon() {
    if (this.isVisible && (this.name || this.path)) {
      this.loaded = false; // reset to false
      const response = await fetch(this.fetchUrl);
      if (!response.ok) {
        throw new Error(`PRISM-UI >>> The SVG Icon ${this.formatName(this.name)} doesn't exist in ${this.fetchUrl}`);
      }
      const svg = await response.text();
      this.svgContent = svg;
      this.loaded = true;
    }
  }

  componentDidRender() {
    if (this.svgContent) {
      const svg = this.element.querySelector('svg');
      // Modifications to the CDN Provided Icons
      if (!this.path && this.cdn !== 'illicons') {
        // NOTE: Illicons are multiple layers and fill is not being touched
        svg?.removeAttribute('style');
        svg?.setAttribute('fill', 'currentcolor');
        if (this.cdn === 'business') {
          // Business Icons do note remove the fill so we need to remove it
          svg?.querySelectorAll('path').forEach(path => {
            path.removeAttribute('fill');
          });
        }
      }
      // a11y Changes
      svg?.setAttribute('focusable', 'false');
      svg?.setAttribute('role', this.svgRole);
      svg?.setAttribute('class', '');
      if (this.label === undefined) {
        svg?.setAttribute('aria-hidden', 'true');
      } else {
        svg?.removeAttribute('aria-hidden');
        svg?.setAttribute('aria-label', this.label);
      }
      this.prismLoad.emit({ name: this.name || this.path, target: this.element, loaded: this.loaded });
    }
  }
  render() {
    return (
      <Host
        class={{
          loaded: this.loaded,
        }}
      >
        <section
          class={{
            contained: this.contained,
            [this.containedTone]: this.contained,
            [this.containedDisplay]: this.contained,
            [this.containedPriority]: this.contained,
            [this.size]: !!this.size,
            [`priority-${this.priority}`]: !!this.priority,
          }}
        >
          {this.svgContent ? (
            <div
              part="container"
              ref={el => (this.element = el as HTMLElement)}
              class={{
                'icon': true,
                [this.tone]: !!this.tone,
                'notifications': this.notifications,
                [`dot-${this.dotTone}`]: !!this.dotTone,
                'icon-source': true,
                [`size-${this.size}`]: !!this.size,
              }}
              innerHTML={DOMPurify.sanitize(this.svgContent, { USE_PROFILES: { svg: true } })}
            ></div>
          ) : null}
        </section>
      </Host>
    );
  }
}
